import {
  createContext,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useAuth } from '~/auth/use-auth';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '~/utils/local-storage';
import { getUserOrganizationsApi } from './api/api';
import type { Organization } from './api/types';

type OrganizationContextProps = {
  organizations: Organization[];
  currentOrganization: Organization | null;
  setCurrentOrganization: (org: Organization) => void;
};

export const OrganizationContext =
  createContext<OrganizationContextProps | null>(null);

type OrganizationProviderProps = {
  children: ReactNode;
};

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrganization, setCurrentOrganization] =
    useState<Organization | null>(null);

  const loadOrganizations = useCallback(async () => {
    const { data: res } = await getUserOrganizationsApi();
    if (!res) return [];

    const orgs = res; // Response is directly an array, not wrapped
    setOrganizations(orgs);

    // Set current organization from localStorage or first organization
    const savedOrgUrn = getLocalStorageItem<string>('current_org_urn');
    const savedOrg = orgs.find((org) => org.urn === savedOrgUrn);
    const defaultOrg = savedOrg || orgs[0] || null;

    setCurrentOrganization(defaultOrg);
    if (defaultOrg) {
      setLocalStorageItem('current_org_urn', defaultOrg.urn);
    }

    return orgs;
  }, []);

  const handleSetCurrentOrganization = useCallback((org: Organization) => {
    setCurrentOrganization(org);
    setLocalStorageItem('current_org_urn', org.urn);
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (user?.metadata?.usage === 'business') {
      loadOrganizations();
    }
  }, [user]);

  return (
    <OrganizationContext.Provider
      value={{
        organizations,
        currentOrganization,
        setCurrentOrganization: handleSetCurrentOrganization,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}
